import {
  FlowConditionalCaseSensitivity,
  type FlowConditionalCondition,
  FlowConditionalFieldDataType,
  FlowConditionalOperation,
} from 'flow-schema';
import { v4 } from 'uuid';

import { assertNever } from 'common';

export function getNewCondition(): FlowConditionalCondition {
  return {
    id: v4(),
    // We are initializing inputValue field with the empty string, even though it will not validate
    inputValue: '',
    dataType: FlowConditionalFieldDataType.String,
    operation: FlowConditionalOperation.Equals,
    caseSensitivity: FlowConditionalCaseSensitivity.ExactMatch,
    value: '',
  };
}

export const DATA_TYPE_OPTIONS = [
  {
    label: 'Boolean',
    value: FlowConditionalFieldDataType.Boolean,
  },
  {
    label: 'String',
    value: FlowConditionalFieldDataType.String,
  },
  {
    label: 'Date',
    value: FlowConditionalFieldDataType.Date,
  },
  {
    label: 'Date & Time',
    value: FlowConditionalFieldDataType.DateTime,
  },
  {
    label: 'Number',
    value: FlowConditionalFieldDataType.Number,
  },
  {
    label: 'Value',
    value: FlowConditionalFieldDataType.Value,
  },
];

const OPERATION_LABEL_BY_OPERATION: {
  [Condition in FlowConditionalCondition as Condition['dataType']]: Record<
    Condition['operation'],
    string
  >;
} = {
  [FlowConditionalFieldDataType.Number]: {
    [FlowConditionalOperation.Equals]: 'equals',
    [FlowConditionalOperation.NotEquals]: 'does not equal',
    [FlowConditionalOperation.InRange]: 'is in range',
    [FlowConditionalOperation.NotInRange]: 'is not in range',
    [FlowConditionalOperation.GreaterThan]: 'is greater than',
    [FlowConditionalOperation.GreaterThanOrEqualTo]: 'is greater than or equal to',
    [FlowConditionalOperation.LessThan]: 'is less than',
    [FlowConditionalOperation.LessThanOrEqualTo]: 'is less than or equal to',
  },
  [FlowConditionalFieldDataType.String]: {
    [FlowConditionalOperation.Equals]: 'equals',
    [FlowConditionalOperation.NotEquals]: 'does not equal',
    [FlowConditionalOperation.Contains]: 'contains',
    [FlowConditionalOperation.DoesNotContain]: 'does not contain',
    [FlowConditionalOperation.StartsWith]: 'starts with',
    [FlowConditionalOperation.DoesNotStartWith]: 'does not start with',
    [FlowConditionalOperation.EndsWith]: 'ends with',
    [FlowConditionalOperation.DoesNotEndWith]: 'does not end with',
    [FlowConditionalOperation.IsOneOf]: 'is one of',
    [FlowConditionalOperation.IsNotOneOf]: 'is not one of',
  },
  [FlowConditionalFieldDataType.Date]: {
    [FlowConditionalOperation.Equals]: 'equals',
    [FlowConditionalOperation.NotEquals]: 'does not equal',
    [FlowConditionalOperation.GreaterThan]: 'is after',
    [FlowConditionalOperation.GreaterThanOrEqualTo]: 'is after or same day as',
    [FlowConditionalOperation.LessThan]: 'is before',
    [FlowConditionalOperation.LessThanOrEqualTo]: 'is before or same day as',
  },
  [FlowConditionalFieldDataType.DateTime]: {
    [FlowConditionalOperation.Equals]: 'exactly same time as',
    [FlowConditionalOperation.NotEquals]: 'not exactly same time as',
    [FlowConditionalOperation.GreaterThan]: 'is after',
    [FlowConditionalOperation.GreaterThanOrEqualTo]: 'is after or exactly same time as',
    [FlowConditionalOperation.LessThan]: 'is before',
    [FlowConditionalOperation.LessThanOrEqualTo]: 'is before or exactly same time as',
  },
  [FlowConditionalFieldDataType.Boolean]: {
    [FlowConditionalOperation.IsTrue]: 'is true',
    [FlowConditionalOperation.IsFalse]: 'is false',
  },
  [FlowConditionalFieldDataType.Value]: {
    [FlowConditionalOperation.Exists]: 'exists',
    [FlowConditionalOperation.DoesNotExist]: 'does not exist',
  },
};

export function getOperationLabel(condition: FlowConditionalCondition) {
  const { dataType, operation } = condition;
  // these type narrowings are necessary to avoid type errors
  if (dataType === FlowConditionalFieldDataType.Boolean) {
    return OPERATION_LABEL_BY_OPERATION[dataType][operation];
  }
  if (dataType === FlowConditionalFieldDataType.String) {
    return OPERATION_LABEL_BY_OPERATION[dataType][operation];
  }
  if (dataType === FlowConditionalFieldDataType.Number) {
    return OPERATION_LABEL_BY_OPERATION[dataType][operation];
  }
  if (dataType === FlowConditionalFieldDataType.Date) {
    return OPERATION_LABEL_BY_OPERATION[dataType][operation];
  }
  if (dataType === FlowConditionalFieldDataType.DateTime) {
    return OPERATION_LABEL_BY_OPERATION[dataType][operation];
  }
  if (dataType === FlowConditionalFieldDataType.Value) {
    return OPERATION_LABEL_BY_OPERATION[dataType][operation];
  }
  assertNever('Unsupported data type', dataType);
}

export const CASE_SENSITIVITY_OPTIONS = [
  {
    label: 'Exact Match',
    value: FlowConditionalCaseSensitivity.ExactMatch,
  },
  {
    label: 'Ignore Case',
    value: FlowConditionalCaseSensitivity.IgnoreCase,
  },
];
